import React, { useEffect, useRef } from 'react';
import { Bed } from 'lucide-react';
import {
  ACCOMMODATION_AD_LABEL, ACCOMMODATION_CLICK_CONTEXT, ACCOMMODATION_CLICK_TARGET,
  ACCOMMODATION_LINK_LABEL, AccommodationCard as AccommodationModel, accommodationCard,
  stageAccommodationCard, proRaceAccommodationCard,
} from '@radevents/shared';
import { track, TrackingEntityType } from '../services/trackingService';
import { umamiTrack } from '../services/analytics';

/**
 * Die Karte „Unterkunft" in der rechten Spalte der Event-Seite — unter dem Wetter, über dem
 * Veranstalter.
 *
 * Abgeleitet wird nichts hier, sondern in [accommodationCard] (`shared/`): Titel, Datumszeile und
 * Adresse sind in der App dieselben. Diese Datei zeichnet nur und meldet den Klick.
 *
 * Die **ganze Karte** ist der Link, nicht nur die letzte Zeile: der Sinn der Karte ist der eine
 * Weg nach draußen, und ein Kasten mit einem kleinen anklickbaren Rest darin lädt zum Danebenklicken
 * ein. `rel="noopener sponsored"` gehört dazu — „sponsored" ist die Auszeichnung, die Google für
 * bezahlte Verweise erwartet, die sichtbare Kennzeichnung steht daneben als Etikett.
 */
interface Props {
  event: Parameters<typeof accommodationCard>[0];
  /** Im Design-Lab: zeichnen, aber nichts melden. */
  preview?: boolean;
  /** `zeile`: als Listenzeile im unteren Teil der Event-Seite auf dem Handy (EventDetailUnten). */
  variant?: 'karte' | 'zeile';
}

interface StageProps {
  stage: Parameters<typeof stageAccommodationCard>[0];
  preview?: boolean;
}

interface RaceProps {
  race: Parameters<typeof proRaceAccommodationCard>[0];
  preview?: boolean;
}

const CARD: React.CSSProperties = { background: '#fff', border: '1px solid #E5E1E1', borderRadius: 14, padding: 20, textDecoration: 'none', display: 'block' };
const MONO_LABEL: React.CSSProperties = { fontFamily: "'Space Mono',monospace", fontSize: 12, letterSpacing: '0.12em', color: '#2C2C2C' };
const MONO_AD: React.CSSProperties = { fontFamily: "'Space Mono',monospace", fontSize: 10, letterSpacing: '0.1em', color: '#B5AFAF' };

/** Die Karte mit fertigem Modell — ohne Tracking, damit das Design-Lab sie zeigen kann. */
export const AccommodationCardView: React.FC<{ card: AccommodationModel; onClick?: () => void; variant?: 'karte' | 'zeile' }> = ({ card, onClick, variant = 'karte' }) => variant === 'zeile' ? (
  // eslint-disable-next-line react/jsx-no-target-blank
  <a href={card.url} target="_blank" rel="noopener sponsored" onClick={onClick} className="edu-zeile edu-zeile--gross" data-testid="accommodation">
    <span className="edu-zeile-icon edu-zeile-icon--marke" aria-hidden><Bed size={18} /></span>
    <span className="edu-zeile-text">
      <strong>{card.title}</strong>
      <span>{card.dateLine}</span>
    </span>
    <span className="edu-anzeige">{ACCOMMODATION_AD_LABEL}</span>
  </a>
) : (
  // Hier steht `noopener sponsored` statt des sonst üblichen `noopener noreferrer`:
  // `noreferrer` unterschlägt auch den Referer, und über den sieht das Partnerprogramm, von
  // welcher Seite der Besucher kam. Die Lücke, gegen die die Regel schützt (`window.opener` in
  // alten Browsern), schließt `noopener` allein.
  // eslint-disable-next-line react/jsx-no-target-blank
  <a href={card.url} target="_blank" rel="noopener sponsored" onClick={onClick} style={CARD} data-testid="accommodation">
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
      <span style={MONO_LABEL}>UNTERKUNFT</span>
      <span style={MONO_AD}>{ACCOMMODATION_AD_LABEL}</span>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 13, marginTop: 12 }}>
      <span style={{ width: 46, height: 46, flexShrink: 0, borderRadius: 12, background: 'rgba(146,44,87,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Bed size={24} color="#922C57" strokeWidth={2} aria-hidden />
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontFamily: "'Hanken Grotesk',sans-serif", fontWeight: 700, fontSize: 16, lineHeight: 1.2, color: '#2C2C2C' }}>{card.title}</span>
        <span style={{ display: 'block', fontFamily: "'Hanken Grotesk',sans-serif", fontSize: 13, color: '#6A6A6A', marginTop: 3 }}>{card.dateLine}</span>
      </span>
    </div>
    <span style={{ display: 'block', fontFamily: "'Hanken Grotesk',sans-serif", fontWeight: 700, fontSize: 13, color: '#922C57', marginTop: 14 }}>{ACCOMMODATION_LINK_LABEL}</span>
  </a>
);

/**
 * Die Karte samt Messung. Event-Seite und Etappenseite hängen sie nur mit ihrer eigenen Entität
 * ein — Beobachter, Schwelle und Meldewege stehen deshalb genau einmal.
 */
const Tracked: React.FC<{
  card: AccommodationModel | null;
  entityType: TrackingEntityType;
  entityId: string;
  preview?: boolean;
  variant?: 'karte' | 'zeile';
}> = ({ card, entityType, entityId, preview, variant }) => {
  const reported = useRef(false);
  const box = useRef<HTMLDivElement>(null);

  /**
   * Die Einblendung wird gemeldet, sobald die Karte wirklich zu sehen war — nicht beim Aufbau der
   * Seite. Die Karte steht weit unten; ohne diese Grenze stünde neben jedem Seitenaufruf eine
   * Einblendung, und die Klickrate wäre eine erfundene Zahl.
   */
  useEffect(() => {
    if (!card || preview || reported.current || !box.current) return;
    const el = box.current;
    if (typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(entries => {
      if (!entries.some(e => e.isIntersecting) || reported.current) return;
      reported.current = true;
      obs.disconnect();
      track(entityType, entityId, 'IMPRESSION', ACCOMMODATION_CLICK_TARGET);
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [card, preview, entityType, entityId]);

  if (!card) return null;

  const onClick = () => {
    if (preview) return;
    track(entityType, entityId, 'CLICK', ACCOMMODATION_CLICK_CONTEXT);
    umamiTrack('unterkunft-klick', { id: entityId, plattform: 'web', startort: card.city });
  };

  return <div ref={box}><AccommodationCardView card={card} onClick={onClick} variant={variant} /></div>;
};

const AccommodationCard: React.FC<Props> = ({ event, preview, variant }) => (
  <Tracked card={accommodationCard(event)} entityType="EVENT" entityId={String(event.id)} preview={preview} variant={variant} />
);

/**
 * Dieselbe Karte auf der Etappenseite eines Profi-Rennens — Zielort, Nacht nach der Etappe
 * (siehe [stageAccommodationCard]).
 *
 * Eigene Entität `PRO_RACE_STAGE` statt `EVENT`: Etappen-Ids und Event-Ids sind verschiedene
 * Zählungen, unter demselben Typ lägen zwei Dinge in einer Zeile.
 */
export const StageAccommodationCard: React.FC<StageProps> = ({ stage, preview }) => (
  <Tracked
    card={stageAccommodationCard(stage)}
    entityType="PRO_RACE_STAGE"
    entityId={String(stage.id)}
    preview={preview}
  />
);

/**
 * Dieselbe Karte auf der **Rennseite** — nur bei Eintagesrennen, siehe
 * [proRaceAccommodationCard]. Gezählt wird auf der Etappe, aus der der Ort stammt: es ist
 * dieselbe Übernachtung, gleich über welche der beiden Seiten sie gefunden wurde.
 */
export const RaceAccommodationCard: React.FC<RaceProps> = ({ race, preview }) => {
  const card = proRaceAccommodationCard(race);
  const stageId = (race.stages ?? [])[0]?.id;
  if (!card || stageId == null) return null;
  return (
    <Tracked card={card} entityType="PRO_RACE_STAGE" entityId={String(stageId)} preview={preview} />
  );
};

export default AccommodationCard;
